// Offline outbox for call outcomes (spec 04 §4.3, acceptance 12). Each entry carries a client id
// (`key` = call_id:version, the server's idempotency key), so a retried submit never logs a call twice.
import type { OutcomePayload } from "../../domain/dialer/outcomes";
import { DialerApiError } from "../api/types";

export const OUTBOX_MAX = 20;
export type OutboxItem = {
  key: string;
  payload: OutcomePayload;
  queued_at: string;
};
export interface OutboxStore {
  all(): Promise<OutboxItem[]>;
  put(i: OutboxItem): Promise<void>;
  remove(key: string): Promise<void>;
}
export type RejectedItem = OutboxItem & { error: DialerApiError };
export type FlushResult = { sent: number; rejected: RejectedItem[] };

export function memoryOutboxStore(): OutboxStore {
  let items: readonly OutboxItem[] = [];
  return {
    async all() {
      return [...items];
    },
    async put(i) {
      items = [...items.filter((x) => x.key !== i.key), i];
    },
    async remove(key) {
      items = items.filter((x) => x.key !== key);
    },
  };
}

/**
 * IndexedDB-backed store: survives reloads. Storage errors are caught and never lose an item:
 * - `open()` fails (private mode, blocked): the operation uses an in-memory pending store;
 * - a transaction fails: that one operation uses the pending store, and the next one retries IDB.
 * Whenever IDB opens again, pending items are merged back into it first.
 */
export function indexedDbOutboxStore(dbName = "gavion-dialer"): OutboxStore {
  const pending = memoryOutboxStore();
  const open = () =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () =>
        req.result.createObjectStore("outbox", { keyPath: "key" });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  const tx = <T>(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction("outbox", mode).objectStore("outbox"));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  const mergePending = async (db: IDBDatabase) => {
    for (const item of await pending.all()) {
      await tx(db, "readwrite", (s) => s.put(item));
      await pending.remove(item.key);
    }
  };
  const withDb = async <T>(
    idb: (db: IDBDatabase) => Promise<T>,
    mem: () => Promise<T>,
  ): Promise<T> => {
    let db: IDBDatabase;
    try {
      db = await open();
    } catch {
      return mem();
    }
    try {
      await mergePending(db);
      return await idb(db);
    } catch {
      return mem();
    } finally {
      db.close();
    }
  };
  return {
    all: () =>
      withDb(
        (db) =>
          tx<OutboxItem[]>(
            db,
            "readonly",
            (s) => s.getAll() as IDBRequest<OutboxItem[]>,
          ),
        () => pending.all(),
      ),
    put: (i) =>
      withDb(
        async (db) => {
          await tx(db, "readwrite", (s) => s.put(i));
        },
        () => pending.put(i),
      ),
    remove: async (key) => {
      await pending.remove(key);
      await withDb(
        async (db) => {
          await tx(db, "readwrite", (s) => s.delete(key));
        },
        async () => undefined,
      );
    },
  };
}

let shared: OutboxStore | null = null;
/**
 * The app's outbox: one instance for every dialer mount, so flushOutbox's single in-flight flush
 * (keyed by store) covers a remounted dialer too.
 */
export function sharedOutboxStore(): OutboxStore {
  shared ??= indexedDbOutboxStore();
  return shared;
}

export const outboxKey = (p: OutcomePayload) => `${p.call_id}:${p.version}`;

export async function enqueueOutcome(
  store: OutboxStore,
  payload: OutcomePayload,
  now: Date,
): Promise<{ queued: true } | { queued: false; reason: "outbox_full" }> {
  const items = await store.all();
  const key = outboxKey(payload);
  if (items.some((i) => i.key === key)) return { queued: true };
  if (items.length >= OUTBOX_MAX)
    return { queued: false, reason: "outbox_full" };
  await store.put({ key, payload, queued_at: now.toISOString() });
  return { queued: true };
}

const inflight = new WeakMap<OutboxStore, Promise<FlushResult>>();

/** Offline or aborted: keep the item and stop, so order is preserved. */
const isNetworkError = (err: unknown): boolean =>
  err instanceof TypeError ||
  (typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError");

/** Oldest first; ties by call id then version, numerically ("9:1" before "10:1"). */
const byQueueOrder = (a: OutboxItem, b: OutboxItem): number =>
  a.queued_at.localeCompare(b.queued_at) ||
  a.payload.call_id - b.payload.call_id ||
  a.payload.version - b.payload.version;

async function flushOnce(
  store: OutboxStore,
  send: (p: OutcomePayload) => Promise<unknown>,
): Promise<FlushResult> {
  const items = (await store.all()).sort(byQueueOrder);
  let sent = 0;
  const rejected: RejectedItem[] = [];
  for (const item of items) {
    try {
      await send(item.payload);
      sent += 1;
    } catch (err) {
      if (isNetworkError(err)) break;
      // Any other failure (e.g. a raw PostgREST error) is a rejection, so it can never block the queue head.
      const error =
        err instanceof DialerApiError
          ? err
          : new DialerApiError("unknown", errorDetail(err));
      rejected.push({ ...item, error });
    }
    await store.remove(item.key);
  }
  return { sent, rejected };
}

const errorDetail = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null) {
    const { code, message } = err as { code?: unknown; message?: unknown };
    return (
      [code, message].filter((x) => typeof x === "string").join(": ") ||
      undefined
    );
  }
  return typeof err === "string" ? err : undefined;
};

/**
 * Sends queued outcomes oldest first. Network errors (TypeError, AbortError) stop the flush and keep
 * the item; any other error drops it and reports it as a DialerApiError ("unknown" when not one
 * already). Concurrent calls on the same store share one flush.
 */
export function flushOutbox(
  store: OutboxStore,
  send: (p: OutcomePayload) => Promise<unknown>,
): Promise<FlushResult> {
  const running = inflight.get(store);
  if (running) return running;
  const run = flushOnce(store, send).finally(() => inflight.delete(store));
  inflight.set(store, run);
  return run;
}
